/**
 * BackgroundProcessor — wraps MediaPipe Selfie Segmentation to produce a
 * processed MediaStream from a raw camera stream, with the background
 * either blurred or replaced by an uploaded image.
 *
 * Why a helper module (and not inline in VideoCallDrawer):
 *  - MediaPipe lifecycle (load → init → close) is fiddly and we want to keep
 *    it out of the React render path.
 *  - The processor needs to survive React re-renders without restarting the
 *    segmentation pipeline. Holding state in a plain JS class lets the
 *    component just call .setMode() / .stop() without React causing flicker.
 *  - Loaded only when first used (no model weights downloaded for users
 *    who never enable background effects).
 *
 * The MediaPipe Selfie Segmentation library is loaded from the jsDelivr CDN
 * on-demand. If the CDN load fails (offline, blocked, etc.) the start() call
 * rejects and the caller can fall back to the raw camera stream.
 */

const MP_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1';
const MP_SCRIPT = `${MP_BASE}/selfie_segmentation.js`;

// Memoise the script load so multiple call drawers reuse the same Promise.
let mediapipeReady = null;

function loadMediaPipe() {
  if (mediapipeReady) return mediapipeReady;
  mediapipeReady = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('MediaPipe requires a browser environment.'));
      return;
    }
    if (window.SelfieSegmentation) {
      resolve(window.SelfieSegmentation);
      return;
    }
    const script = document.createElement('script');
    script.src = MP_SCRIPT;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (window.SelfieSegmentation) {
        resolve(window.SelfieSegmentation);
      } else {
        reject(new Error('MediaPipe loaded but SelfieSegmentation global missing.'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load MediaPipe from CDN.'));
    document.head.appendChild(script);
  });
  return mediapipeReady;
}

export default class BackgroundProcessor {
  constructor() {
    this.mode = 'none';            // 'none' | 'blur' | 'image'
    this.backgroundImage = null;   // HTMLImageElement when mode === 'image'
    this.sourceStream = null;
    this.sourceVideo = null;       // Hidden <video> playing the raw camera
    this.canvas = null;            // Output canvas — MediaPipe draws here
    this.canvasCtx = null;
    this.outputStream = null;
    this.segmentation = null;
    this.running = false;
    this.rafHandle = null;
    this.lastFrameStamp = 0;
  }

  /**
   * Initialise the pipeline. Call once before setMode('blur'|'image').
   * Returns the processed MediaStream (a captureStream off the canvas).
   *
   * The output stream's video track replaces the original camera track on
   * the RTCPeerConnection (via sender.replaceTrack) — that's what makes the
   * remote peer see the effect, not just the local preview.
   */
  async start(sourceStream) {
    if (this.running) return this.outputStream;
    const SelfieSegmentation = await loadMediaPipe();

    this.sourceStream = sourceStream;
    const videoTrack = sourceStream.getVideoTracks()[0];
    if (!videoTrack) throw new Error('Source stream has no video track.');

    // Source <video> — fed by the raw camera so MediaPipe has something to read.
    this.sourceVideo = document.createElement('video');
    this.sourceVideo.muted = true;
    this.sourceVideo.playsInline = true;
    this.sourceVideo.autoplay = true;
    this.sourceVideo.srcObject = sourceStream;
    await this.sourceVideo.play().catch(() => {});

    // Wait for metadata so we know the source resolution.
    if (!this.sourceVideo.videoWidth) {
      await new Promise(res => {
        const handler = () => {
          this.sourceVideo.removeEventListener('loadedmetadata', handler);
          res();
        };
        this.sourceVideo.addEventListener('loadedmetadata', handler);
      });
    }

    // Output canvas matches the source resolution so the remote receives the
    // same framing the camera is actually capturing.
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.sourceVideo.videoWidth || 640;
    this.canvas.height = this.sourceVideo.videoHeight || 480;
    this.canvasCtx = this.canvas.getContext('2d');

    this.segmentation = new SelfieSegmentation({
      locateFile: file => `${MP_BASE}/${file}`,
    });
    this.segmentation.setOptions({
      // modelSelection 1 = "landscape" model — higher quality, slightly more
      // expensive. modelSelection 0 = "general" — faster, less accurate.
      // For a 2-person video call landscape is the right trade-off.
      modelSelection: 1,
      selfieMode: false,
    });
    this.segmentation.onResults(results => this.onResults(results));

    // 30 fps cap is enough for video conferencing and saves a lot of CPU
    // compared to free-running rAF on a 120Hz display.
    this.outputStream = this.canvas.captureStream(30);

    // Keep the audio track from the original stream so we don't need to
    // shuffle two streams in the consumer. The mute toggle still works
    // because audio.enabled is set on the original track which we forward.
    sourceStream.getAudioTracks().forEach(track => {
      this.outputStream.addTrack(track);
    });

    this.running = true;
    this.loop();
    return this.outputStream;
  }

  loop = () => {
    if (!this.running) return;
    // Throttle to ~30 fps — sending more than that to MediaPipe is just wasted
    // GPU work and tends to make Safari struggle.
    const now = performance.now();
    if (now - this.lastFrameStamp >= 33 && this.sourceVideo.readyState >= 2) {
      this.lastFrameStamp = now;
      this.segmentation
        .send({ image: this.sourceVideo })
        .catch(err => {
          // Don't tear down the loop on a single bad frame; MediaPipe can
          // recover. Log so we notice if it's chronic.
          // eslint-disable-next-line no-console
          console.warn('SelfieSegmentation send failed:', err);
        });
    }
    this.rafHandle = requestAnimationFrame(this.loop);
  };

  onResults(results) {
    if (!this.canvasCtx || !this.canvas) return;
    const ctx = this.canvasCtx;
    const { width, height } = this.canvas;

    ctx.save();
    ctx.clearRect(0, 0, width, height);

    // Step 1: draw the segmentation mask. Then use it as a clipping mask for
    // the person, and the inverse as a clip for the background. The
    // composite ops below are the canonical MediaPipe recipe.
    ctx.drawImage(results.segmentationMask, 0, 0, width, height);

    ctx.globalCompositeOperation = 'source-out';
    if (this.mode === 'blur') {
      // For blur we paint a *blurred copy* of the same frame as the background.
      // CanvasRenderingContext2D.filter is supported in all modern browsers we
      // care about (Chrome, Edge, Firefox, Safari 14.1+). Safari < 14 will
      // skip the blur and just paint the original frame — better than crashing.
      ctx.filter = 'blur(12px)';
      ctx.drawImage(results.image, 0, 0, width, height);
      ctx.filter = 'none';
    } else if (this.mode === 'image' && this.backgroundImage) {
      // Cover-fit the image into the canvas so portrait/landscape uploads
      // both look reasonable.
      const img = this.backgroundImage;
      const sourceAspect = img.naturalWidth / img.naturalHeight;
      const targetAspect = width / height;
      let drawW, drawH, drawX, drawY;
      if (sourceAspect > targetAspect) {
        drawH = height;
        drawW = height * sourceAspect;
        drawX = (width - drawW) / 2;
        drawY = 0;
      } else {
        drawW = width;
        drawH = width / sourceAspect;
        drawX = 0;
        drawY = (height - drawH) / 2;
      }
      ctx.drawImage(img, drawX, drawY, drawW, drawH);
    } else {
      // Fallback if mode somehow changed mid-frame: keep the raw frame.
      ctx.drawImage(results.image, 0, 0, width, height);
    }

    // Step 2: composite the person on top, untouched.
    ctx.globalCompositeOperation = 'destination-atop';
    ctx.drawImage(results.image, 0, 0, width, height);

    ctx.restore();
  }

  setMode(mode, image = null) {
    this.mode = mode;
    if (image) this.backgroundImage = image;
  }

  /**
   * Stop the pipeline and release everything. Idempotent.
   */
  stop() {
    this.running = false;
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    if (this.segmentation) {
      try { this.segmentation.close(); } catch (e) { /* noop */ }
      this.segmentation = null;
    }
    if (this.sourceVideo) {
      this.sourceVideo.srcObject = null;
      this.sourceVideo = null;
    }
    if (this.outputStream) {
      this.outputStream.getTracks().forEach(t => {
        // Don't stop tracks we don't own (e.g. the audio track forwarded from
        // the source stream); the consumer manages its own stream lifecycle.
        if (t.kind === 'video') t.stop();
      });
      this.outputStream = null;
    }
    this.canvas = null;
    this.canvasCtx = null;
    this.sourceStream = null;
    this.backgroundImage = null;
    this.mode = 'none';
  }
}
